package songwritersnotebook.app.viewmodels

import androidx.compose.ui.text.TextRange
import songwritersnotebook.app.data.Song
import songwritersnotebook.app.data.GuitarTab
import songwritersnotebook.app.data.RecordingMetadata
import songwritersnotebook.app.data.SavedChord
import songwritersnotebook.app.database.UploadStatus

enum class FocusPivot {
    TOP,
    BOTTOM
}

// Represents a request to focus a specific UI element in the editor
data class FocusRequest(
    val targetId: String,
    val isSectionTitle: Boolean = false,
    val cursorPosition: TextRange = TextRange.Zero,
    val horizontalOffset: Float? = null,
    val focusPivot: FocusPivot? = null
)

// Data class to hold information about a specific chord instance in a line
data class ChordInLine(
    val name: String,
    val index: Int, // The nth chord in the line (0, 1, 2...)
    val range: IntRange // The character range in the original string
)

// Represents the item currently selected for actions like moving or deleting
data class ItemSelection(
    val id: String,
    val sectionIndex: Int,
    val lineIndex: Int? = null, // null if it's a section
    val isMerge: Boolean = false,
    val mergeText: String? = null
)

enum class ChordDisplayMode { Large, Small, Off }

enum class ChordPlacement { Inline, Above }

enum class WordFinderTab { RHYMES, SYNONYMS }

// Data class for editor-specific settings
data class EditorSettings(
    val showSectionHeaders: Boolean = true,
    val chordDisplay: ChordDisplayMode = ChordDisplayMode.Large,
    val chordPlacement: ChordPlacement = ChordPlacement.Inline,
    val syllableCountEnabled: Boolean = false,
    val rhymingEnabled: Boolean = false,
    val showSlantRhymes: Boolean = false,
    val endOfLineOnly: Boolean = false,
    val showPianoInfo: Boolean = false,
    val showGuitarInfo: Boolean = false,
    val showTabs: Boolean = true,
    val isLeftHanded: Boolean = false,
    val harmonyConfirmationRequired: Boolean = true,
    val harmonyVoiceEnabled: Boolean = true
)

data class RecordingTarget(
    val id: String,
    val name: String,
    val outputFile: String, // Changed to String for KMP compatibility (File is JVM)
    val idToReplace: String? = null,
    val lineText: String? = null,
    val associatedTabId: String? = null,
    val parentTargetId: String? = null
)

enum class SaveStatus { IDLE, SYNCING, SAVING, SAVED, ERROR, OFFLINE }

enum class RhymeType { PERFECT, SLANT }

data class RhymeInfo(
    val type: RhymeType,
    val colorHex: String
)

data class ChordCreatorUiState(
    val keyboardOctave: Int = 4,
    val manualKeys: List<String> = emptyList(),
    val selectedInstrument: String = "piano",
    val selectedChordRoot: String = "C",
    val selectedChordQuality: String = "maj",
    val selectedQualityCategoryIndex: Int = 0,
    val guitarTuning: List<String> = songwritersnotebook.app.utils.ChordUtils.DEFAULT_GUITAR_TUNING,
    val guitarCapo: Int = 0,
    val currentTab: List<Any> = List(6) { "0" },
    val isCustom: Boolean = false
)

// Represents the entire state for the EditorScreen UI
data class EditorUiState(
    val song: Song? = null,
    val chordLibrary: List<String> = emptyList(),
    val orderedChordNames: List<String> = emptyList(),
    val chordsInUse: Set<String> = emptySet(),
    val selectedChordToPlace: String? = null,
    val activeProgressionId: String? = null,
    val activeProgressionIndex: Int = 0,
    val chordToEdit: String? = null,
    val chordToDelete: String? = null,
    val showChordCreatorDialog: Boolean = false,
    val showProgressionBuilderDialog: Boolean = false,
    val progressionToEditId: String? = null,
    val nextFocusRequest: FocusRequest? = null,
    val lastFocusedLine: Pair<Int, Int>? = null,
    val selectedItem: ItemSelection? = null,
    val itemToDelete: ItemSelection? = null,
    val selectedLineChords: List<ChordInLine> = emptyList(),
    val activeChord: ChordInLine? = null,
    val validInsertionPoints: List<Int> = emptyList(),
    val editorSettings: EditorSettings = EditorSettings(),
    val rhymeData: Map<String, RhymeInfo> = emptyMap(),
    val showRecordingSheet: Boolean = false,
    val recordingTarget: RecordingTarget? = null,
    val playingRecordingId: String? = null,
    val activeRecordingLineId: String? = null,
    val isMetronomeEnabled: Boolean = false,
    val isMetronomeMuted: Boolean = false,
    val isTempoPreviewActive: Boolean = false,
    val currentMetronomeBeat: Int = -1,
    val keyboardOctave: Int = 4,
    val manualKeys: List<String> = emptyList(),
    val selectedInstrument: String = "piano",
    val currentTab: List<Any> = List(6) { "0" },
    val guitarTuning: List<String> = songwritersnotebook.app.utils.ChordUtils.DEFAULT_GUITAR_TUNING,
    val guitarCapo: Int = 0,
    val strumOffset: Float = 40f,
    val showAiAssistantSheet: Boolean = false,
    val isAiSheetFullHeight: Boolean = true,
    val aiAssistantSelectedTab: Int = 0,
    val selectedChordRoot: String = "C",
    val selectedChordQuality: String = "maj",
    val selectedQualityCategoryIndex: Int = 0,
    val isCustom: Boolean = false,
    val isGeneratingLyrics: Boolean = false,
    val showClearChatDialog: Boolean = false,
    val isGeneratingChords: Boolean = false,
    val fretWindowStart: Int = 0,
    val isFavorited: Boolean = false,
    val selectedFavoriteChord: SavedChord? = null,
    val favoriteProgressions: List<songwritersnotebook.app.data.SavedProgression> = emptyList(),
    val selectedFavoriteProgression: songwritersnotebook.app.data.SavedProgression? = null,
    val saveStatus: SaveStatus = SaveStatus.IDLE,
    val showAiLimitDialog: Boolean = false,
    val aiCreditProducts: List<songwritersnotebook.app.billing.ProductInfo> = emptyList(),
    val showAddMediaDialog: Boolean = false,
    val showTabEditorSheet: Boolean = false,
    val showLineMediaSheet: Boolean = false,
    val initialMediaTabIndex: Int? = null,
    val showWordFinderPopup: Boolean = false,
    val wordFinderSearchTerm: String = "",
    val wordFinderSelectedTab: WordFinderTab = WordFinderTab.RHYMES,
    val rhymeResults: List<String> = emptyList(),
    val synonymResults: List<String> = emptyList(),
    val isWordFinderLoading: Boolean = false,
    val wordFinderError: String? = null,
    val editingTab: GuitarTab? = null,
    val editingTabTargetId: String? = null,
    val cloudSyncError: String? = null,
    val uploadStatus: Map<String, UploadStatus> = emptyMap(),
    val showSignInPrompt: Boolean = false,
    val showQuotaExceededDialog: Boolean = false,
    
    // Move Recording
    val showMoveRecordingDialog: Boolean = false,
    val recordingToMove: RecordingMetadata? = null,
    
    // Recordings List Sheet
    val showRecordingsListSheet: Boolean = false,
    
    val audioSyncProgress: Float? = null,
    val isExporting: Boolean = false,
    val userProfile: songwritersnotebook.app.data.UserProfile? = null,
    val presence: Map<String, songwritersnotebook.app.data.UserPresence> = emptyMap(),
    val cursorPosition: Int = 0,
    val activeSectionId: String? = null,
    val activeLineId: String? = null,
    
    // Version History
    val isLoading: Boolean = false,
    val isSaving: Boolean = false,
    val isShared: Boolean = false,
    val isViewer: Boolean = false,
    val versions: List<songwritersnotebook.app.data.SongVersion> = emptyList(),
    val versionPreview: Song? = null,
    val showVersionHistorySheet: Boolean = false,
    val showUpgradeDialog: Boolean = false,
    val upgradeMessage: String = "",
    val showVersionHistoryOwnerInfoDialog: Boolean = false,
    
    // Harmony AI
    val isHarmonyListening: Boolean = false,
    val harmonyTranscript: String = "",
    val isHarmonyThinking: Boolean = false,
    val harmonySpokenResponse: String? = null,
    val harmonyChatHistory: List<HarmonyChatMessage> = emptyList(),
    val harmonyPendingActions: List<HarmonyPendingAction> = emptyList(),
    
    val canUndo: Boolean = false,
    val canRedo: Boolean = false,
    val deviceId: String = "",
    
    // Collaborator Management
    val selectedPresence: songwritersnotebook.app.data.UserPresence? = null,
    val showCollaboratorActionDialog: Boolean = false,
    val collaboratorActionType: CollaboratorAction? = null,
    val shouldExit: Boolean = false
)

enum class CollaboratorAction { REMOVE, BLOCK }

data class HarmonyPendingAction(
    val toolName: String,
    val args: kotlinx.serialization.json.JsonElement,
    val id: String = com.benasher44.uuid.uuid4().toString()
)

data class HarmonyChatMessage(
    val text: String,
    val isUser: Boolean,
    val id: String = com.benasher44.uuid.uuid4().toString()
)
